import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { fmtH } from '../../lib/currency';
import { localeDates, trf } from '../../lib/i18n';
import { journalDuChantier } from '../../lib/journal';
import { C } from '../../theme/palette';
import { PhotoAgrandie } from '../../components/PhotoAgrandie.jsx';

/** « 2026-10-12 » → « lundi 12 octobre 2026 », dans la langue de l'écran. */
const jourLisible = (iso) => {
  const [a, m, j] = iso.split('-').map(Number);
  return new Date(a, m - 1, j).toLocaleDateString(localeDates(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
};

/**
 * Journal du chantier : pour chaque journée, chaque ouvrier venu sur place,
 * ses heures, ce qu'il a fait, le matériel pris au dépôt (à quelle heure) et
 * les photos qu'il a prises ce jour-là, avec l'adresse du chantier. Chaque
 * chose dans sa case : heures, travaux, matériel, photos.
 */
export function JournalChantier({ ch, employes }) {
  const jours = journalDuChantier(ch, employes);
  const adresse = [ch.adr, ch.npa].filter(Boolean).join(', ');
  if (jours.length === 0)
    return (
      <Card>
        <div style={{ padding: 16, textAlign: 'center', fontSize: 11, color: C.muted }}>
          Rien encore sur ce chantier. Les heures pointées par les ouvriers, ce qu&apos;ils notent
          avoir fait, le matériel pris au dépôt et leurs photos s&apos;inscrivent ici, jour par
          jour.
        </div>
      </Card>
    );
  return (
    <div>
      {jours.map((j) => (
        <Card key={j.date} style={{ marginBottom: 11 }}>
          <Bar
            icon="📅"
            title={jourLisible(j.date)}
            color={C.navy}
            sub={
              j.ouvriers.length +
              ' · ' +
              fmtH(j.ouvriers.reduce((s, o) => s + o.total, 0)) +
              (adresse ? ' · ' + adresse : '')
            }
          />
          <div style={{ padding: '4px 13px 12px' }}>
            {adresse ? (
              <a
                href={
                  'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(adresse)
                }
                target="_blank"
                rel="noreferrer"
                style={{ display: 'block', fontSize: 10, color: C.teal, margin: '6px 0 2px' }}
              >
                📍 {adresse}
              </a>
            ) : null}
            {j.ouvriers.map((o) => (
              <div
                key={o.cle}
                style={{ borderTop: '1px solid ' + C.border, padding: '10px 0 4px' }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    fontSize: 12,
                    fontWeight: 800,
                    color: C.ink,
                  }}
                >
                  <span>👷 {o.nom}</span>
                  {o.pointages.length > 0 ? <span>{fmtH(o.total)}</span> : null}
                </div>
                {o.pointages.map((p, i) => (
                  <div key={p.id ?? i} style={{ fontSize: 10.5, color: C.grey, marginTop: 3 }}>
                    {'⏱ ' + (p.entree || '—') + ' → ' + (p.sortie || 'en cours')}
                    {+p.pause > 0 ? ' · pause ' + p.pause + ' min' : ''}
                    {p.sortie ? ' · ' + fmtH(p.heures) : ''}
                    {p.onSiteEntree === false ? ' · ⚠️ hors du chantier à l’arrivée' : ''}
                  </div>
                ))}
                {o.taches.length > 0 ? (
                  <div
                    style={{
                      marginTop: 7,
                      background: C.bg,
                      borderRadius: 8,
                      padding: '8px 10px',
                      fontSize: 11,
                      color: C.ink,
                      lineHeight: 1.5,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    <b>📝 Travaux réalisés</b>
                    {o.taches.map((t, i) => (
                      <div key={i}>{t}</div>
                    ))}
                  </div>
                ) : o.pointages.length > 0 ? (
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 5 }}>
                    Aucune tâche notée ce jour-là.
                  </div>
                ) : null}
                {o.rapports
                  .filter((r) => r.meteo || r.materiel)
                  .map((r, i) => (
                    <div key={i} style={{ fontSize: 10, color: C.grey, marginTop: 4 }}>
                      {[r.meteo, r.materiel ? '🧰 ' + r.materiel : ''].filter(Boolean).join(' · ')}
                    </div>
                  ))}
                {o.materiel.length > 0 ? (
                  <div
                    data-testid="materiel-du-jour"
                    style={{
                      marginTop: 7,
                      background: C.navyXL,
                      borderRadius: 8,
                      padding: '8px 10px',
                      fontSize: 11,
                      color: C.ink,
                      lineHeight: 1.5,
                    }}
                  >
                    <b>📦 Matériel du dépôt</b>
                    {o.materiel.map((m, i) => (
                      <div key={m.id ?? i}>
                        {(m.heure ? m.heure + ' · ' : '') +
                          (+m.qte < 0
                            ? trf('↩️ rapporté : {0} {1} {2}', -m.qte, m.u || '', m.nom || m.d)
                            : trf('pris : {0} {1} {2}', +m.qte, m.u || '', m.nom || m.d))}
                      </div>
                    ))}
                  </div>
                ) : null}
                {o.photos.length > 0 ? (
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fill, minmax(88px, 1fr))',
                      gap: 6,
                      marginTop: 8,
                    }}
                  >
                    {o.photos.map((ph, i) => (
                      <div
                        key={ph.id ?? i}
                        style={{ textDecoration: 'none', color: C.muted, fontSize: 9 }}
                      >
                        {ph.src ? (
                          <PhotoAgrandie src={ph.src} alt={ph.note || ph.label || ''}>
                            <img
                              src={ph.src}
                              alt={ph.note || ph.label || ''}
                              style={{
                                width: '100%',
                                aspectRatio: '1',
                                objectFit: 'cover',
                                borderRadius: 8,
                                display: 'block',
                              }}
                            />
                          </PhotoAgrandie>
                        ) : null}
                        <div style={{ marginTop: 2 }}>
                          {/* La légende qui reprend les travaux n'est pas répétée. */}
                          {[ph.h, o.taches.includes(String(ph.note || '').trim()) ? '' : ph.note]
                            .filter(Boolean)
                            .join(' · ')}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
